// Diagnósticos · Programas: para qué usa la org los diagnósticos (interno, clientes,
// captación) + su plantilla. La validación real es del backend (normalize_template):
// aquí solo se arma el JSON y se muestran sus avisos/errores.
import { useCallback, useEffect, useState } from 'react';
import { Archive, Gift, Layers, Pencil, Plus, RefreshCw, Users } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import * as api from '../api';
import type { DiagProgram, Proposito } from '../types';
import {
  Btn, Chip, Empty, ErrorBox, Field, Panel, PROPOSITO_HINT, PROPOSITO_LABEL, Spinner, STATUS_LABEL, TIPO_LABEL,
  inputCls, statusTone,
} from './ui';

type Form = Record<string, string | boolean>;

const DEST_BY_PURPOSE: Record<Proposito, { key: string; label: string }[]> = {
  interno: [{ key: 'memoria', label: 'Memoria de la org' }, { key: 'backlog', label: 'Backlog' },
    { key: 'reporte_cliente', label: 'Reporte' }],
  cliente: [{ key: 'reporte_cliente', label: 'Reporte al cliente' }, { key: 'crm', label: 'CRM' },
    { key: 'ventas', label: 'Ventas' }],
  captacion: [{ key: 'crm', label: 'CRM' }, { key: 'email_marketing', label: 'Email marketing (con consentimiento)' },
    { key: 'ventas', label: 'Ventas (embudo «compraron»)' }, { key: 'reporte_cliente', label: 'Reporte' }],
};

const TEXT_KEYS = [
  'tipo', 'enfoque', 'interview_org', 'brand', 'consultant_name', 'invite_subject', 'invite_body', 'offer_cta',
  'offer_url', 'offer_label', 'class_url', 'class_label', 'offer_note', 'event_slug',
];
const NUM_KEYS = ['max_invitados', 'auto_release_max_wait_hours', 'invite_reminder_hours', 'retention_days', 'daily_cap', 'max_projects'];
const LIST_KEYS = ['deliverables', 'alert_emails', 'lead_sources'];
const CAPTACION_ONLY = ['lead_sources', 'valid_until', 'daily_cap', 'max_projects'];

function toForm(p?: DiagProgram): Form {
  const c = (p?.config ?? {}) as Record<string, unknown>;
  const f: Form = {
    name: p?.name ?? '', proposito: p?.proposito ?? 'cliente', status: p?.status ?? 'pausado',
    description: p?.description ?? '',
  };
  TEXT_KEYS.concat(NUM_KEYS).forEach((k) => { f[k] = c[k] === undefined || c[k] === null ? '' : String(c[k]); });
  LIST_KEYS.forEach((k) => { f[k] = Array.isArray(c[k]) ? (c[k] as string[]).join(', ') : ''; });
  f.valid_until = typeof c.valid_until === 'string' ? (c.valid_until as string).slice(0, 10) : '';
  f.auto_release_result = c.auto_release_result === undefined ? true : Boolean(c.auto_release_result);
  f.consent_required = c.consent_required === undefined ? true : Boolean(c.consent_required);
  (Array.isArray(c.destinations) ? (c.destinations as string[]) : []).forEach((d) => { f[`dest_${d}`] = true; });
  return f;
}

/** Form → config. En edición, un campo vacío manda null (vuelve a heredar de la org/plataforma). */
function toConfig(f: Form, editing: boolean): Record<string, unknown> {
  const proposito = f.proposito as Proposito;
  const out: Record<string, unknown> = {};
  const put = (k: string, v: unknown, empty: boolean) => {
    if (!empty) out[k] = v;
    else if (editing) out[k] = null;
  };
  TEXT_KEYS.forEach((k) => { const v = String(f[k] ?? '').trim(); put(k, v, !v); });
  NUM_KEYS.forEach((k) => { const v = String(f[k] ?? '').trim(); put(k, v ? Number(v) : null, !v); });
  LIST_KEYS.forEach((k) => {
    const v = String(f[k] ?? '').split(',').map((x) => x.trim()).filter(Boolean);
    put(k, v, !v.length);
  });
  const vu = String(f.valid_until ?? '').trim();
  put('valid_until', vu, !vu);
  out.auto_release_result = Boolean(f.auto_release_result);
  out.consent_required = Boolean(f.consent_required);
  out.destinations = DEST_BY_PURPOSE[proposito].map((d) => d.key).filter((k) => f[`dest_${k}`]);
  if (proposito !== 'captacion') CAPTACION_ONLY.forEach((k) => { delete out[k]; });
  return out;
}

function ProgramForm({ initial, onClose, onSaved }: { initial?: DiagProgram; onClose: () => void; onSaved: (p: DiagProgram) => void }) {
  const [f, setF] = useState<Form>(() => toForm(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const editing = Boolean(initial);
  const proposito = f.proposito as Proposito;
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggle = (k: string) => () => setF((x) => ({ ...x, [k]: !x[k] }));
  const inherited = (k: string) => {
    const src = initial?.effective.sources[k];
    const val = initial?.effective.values[k];
    if (!initial || src === 'program' || val === '' || val === null || val === undefined) return undefined;
    return `Hereda (${src === 'org' ? 'org' : 'plataforma'}): ${Array.isArray(val) ? val.join(', ') : String(val)}`;
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const body: Record<string, unknown> = {
        name: String(f.name).trim(), status: f.status, description: String(f.description),
        config: toConfig(f, editing),
      };
      const saved = editing
        ? await api.updateProgram(initial!.id, body)
        : await api.createProgram({ ...body, proposito });
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const text = (k: string, label: string, hint?: string, placeholder?: string) => (
    <Field label={label} hint={hint ?? inherited(k)}>
      <input className={inputCls} value={String(f[k] ?? '')} onChange={set(k)} placeholder={placeholder} />
    </Field>
  );

  return (
    <Modal open onClose={onClose} size="xl" title={editing ? `Editar programa · ${initial!.name}` : 'Nuevo programa'}
      footer={<div className="flex justify-end gap-2"><Btn onClick={onClose}>Cancelar</Btn><Btn kind="primary" busy={busy} onClick={save} disabled={!String(f.name).trim()}>Guardar</Btn></div>}>
      <div className="space-y-5">
        <ErrorBox error={error} />
        <div className="grid gap-3 sm:grid-cols-3">
          {(['interno', 'cliente', 'captacion'] as Proposito[]).map((p) => (
            <button key={p} type="button" disabled={editing}
              onClick={() => setF((x) => ({ ...x, proposito: p }))}
              className={`rounded-xl border p-3 text-left transition ${proposito === p ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'} ${editing && proposito !== p ? 'opacity-40' : ''}`}>
              <div className="text-sm font-semibold text-foreground">{PROPOSITO_LABEL[p]}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">{PROPOSITO_HINT[p]}</div>
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {text('name', 'Nombre del programa', '', 'p. ej. Diagnóstico gratis · Laboratorio')}
          <Field label="Estado" hint={proposito === 'captacion' ? 'Activo = los leads con consentimiento reciben su diagnóstico.' : undefined}>
            <select className={inputCls} value={String(f.status)} onChange={set('status')}>
              <option value="pausado">Pausado</option>
              <option value="activo">Activo</option>
              {editing && <option value="archivado">Archivado</option>}
            </select>
          </Field>
        </div>
        <Field label="Descripción"><textarea className={inputCls} rows={2} value={String(f.description)} onChange={set('description')} /></Field>

        <Panel title="Plantilla del diagnóstico">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tipo" hint={inherited('tipo')}>
              <select className={inputCls} value={String(f.tipo)} onChange={set('tipo')}>
                <option value="">(heredar)</option>
                {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            {text('enfoque', 'Enfoque', undefined, 'qué se implementa / diseña / mide')}
            {text('interview_org', 'Entrevistador (org)', inherited('interview_org') ?? 'Vacío = neutral «diagnostico». Solo tu org o un entrevistador neutral.')}
            {text('max_invitados', 'Invitados máx. por dueño (0–5)')}
            {text('brand', 'Marca')}
            {text('consultant_name', 'Quién invita (nombre en el correo)')}
            {text('deliverables', 'Entregables', 'Separados por coma')}
            {text('retention_days', 'Retención (días)')}
          </div>
          <div className="mt-3 grid gap-3">
            {text('invite_subject', 'Asunto de la invitación', 'Variables: {nombre} {enlace} {consultor} {cliente}')}
            <Field label="Cuerpo de la invitación" hint={inherited('invite_body')}>
              <textarea className={inputCls} rows={4} value={String(f.invite_body)} onChange={set('invite_body')} />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-foreground">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={Boolean(f.consent_required)} onChange={toggle('consent_required')} /> Consentimiento requerido</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={Boolean(f.auto_release_result)} onChange={toggle('auto_release_result')} /> Envío automático del resultado</label>
          </div>
          {Boolean(f.auto_release_result) && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {text('auto_release_max_wait_hours', 'Espera máx. tras analizar al dueño (h)')}
              {text('alert_emails', 'Avisos si falla el envío', 'Correos separados por coma')}
            </div>
          )}
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-foreground">Destinos de los resultados</div>
            <div className="flex flex-wrap gap-3 text-sm text-foreground">
              {DEST_BY_PURPOSE[proposito].map((d) => (
                <label key={d.key} className="inline-flex items-center gap-2"><input type="checkbox" checked={Boolean(f[`dest_${d.key}`])} onChange={toggle(`dest_${d.key}`)} /> {d.label}</label>
              ))}
            </div>
          </div>
        </Panel>

        {proposito === 'captacion' && (
          <Panel title="Captación (regalo a leads)">
            <div className="grid gap-3 sm:grid-cols-2">
              {text('lead_sources', 'Fuentes de lead', 'Lead.source que disparan el regalo, separadas por coma (obligatorio para activar)')}
              <Field label="Vigente hasta" hint={inherited('valid_until')}>
                <input type="date" className={inputCls} value={String(f.valid_until)} onChange={set('valid_until')} />
              </Field>
              {text('daily_cap', 'Tope diario de regalos')}
              {text('max_projects', 'Cupo total de regalos')}
              {text('invite_reminder_hours', 'Recordatorio «invita a tu equipo» (h)', '0 = apagado. Por defecto 24 h después de que el dueño termina.')}
            </div>
          </Panel>
        )}

        <Panel title="Oferta en el resultado (CTA)">
          <div className="grid gap-3 sm:grid-cols-2">
            {text('offer_cta', 'Texto de la oferta')}
            {text('offer_url', 'Enlace de la oferta', 'Solo https://')}
            {text('offer_label', 'Botón de la oferta')}
            {text('class_url', 'Enlace de la clase', 'Solo https://')}
            {text('class_label', 'Botón de la clase')}
            {text('event_slug', 'Evento de venta (slug)', 'Para contar «compraron» en el embudo (órdenes pagadas)')}
          </div>
          <div className="mt-3">{text('offer_note', 'Nota de la oferta')}</div>
        </Panel>
      </div>
    </Modal>
  );
}

export function ProgramsView() {
  const [programs, setPrograms] = useState<DiagProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<DiagProgram | null | 'new'>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPrograms((await api.listPrograms(showArchived)).programs);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { void load(); }, [load]);

  const archive = async (p: DiagProgram) => {
    if (!window.confirm(`¿Archivar «${p.name}»? Deja de crear proyectos; los existentes no se tocan.`)) return;
    try {
      await api.archiveProgram(p.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const icon = (p: Proposito) => (p === 'captacion' ? <Gift className="h-4 w-4" /> : p === 'cliente' ? <Users className="h-4 w-4" /> : <Layers className="h-4 w-4" />);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Programas de diagnóstico</h2>
          <p className="text-sm text-muted-foreground">Para qué usas los diagnósticos y con qué plantilla. Los proyectos heredan de su programa.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={showArchived} onChange={() => setShowArchived((x) => !x)} /> Archivados</label>
          <Btn onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Btn>
          <Btn kind="primary" onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Nuevo programa</Btn>
        </div>
      </div>
      <ErrorBox error={error} />
      {warnings.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          {warnings.map((w) => <div key={w}>{w}</div>)}
        </div>
      )}
      {loading ? <Spinner /> : programs.length === 0 ? (
        <Empty>Aún no hay programas. Crea uno para tus clientes, para tu propia org o para regalar diagnósticos a tus leads.</Empty>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {programs.map((p) => {
            const v = p.effective.values;
            return (
              <div key={p.id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-foreground">{icon(p.proposito)}<span className="font-semibold">{p.name}</span></div>
                  <Chip tone={statusTone(p.status)}>{STATUS_LABEL[p.status] ?? p.status}</Chip>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Chip tone="primary">{PROPOSITO_LABEL[p.proposito]}</Chip>
                  <Chip>{TIPO_LABEL[String(v.tipo)] ?? String(v.tipo)}</Chip>
                  <Chip>{p.projects_total ?? 0} proyecto(s)</Chip>
                  {p.legacy_mode && <Chip tone="warn">Proyectos en «{p.project_org}»</Chip>}
                </div>
                {p.description && <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>}
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">Entrevistador</dt><dd className="text-foreground">{String(v.interview_org || '—')}</dd>
                  <dt className="text-muted-foreground">Invitados máx.</dt><dd className="text-foreground">{String(v.max_invitados)}</dd>
                  {p.proposito === 'captacion' && <>
                    <dt className="text-muted-foreground">Fuentes</dt><dd className="truncate text-foreground">{(v.lead_sources as string[] | undefined)?.join(', ') || '—'}</dd>
                    <dt className="text-muted-foreground">Vigente hasta</dt><dd className="text-foreground">{String(v.valid_until || '—').slice(0, 10)}</dd>
                    <dt className="text-muted-foreground">Envío automático</dt><dd className="text-foreground">{v.auto_release_result ? 'Sí' : 'No'}</dd>
                  </>}
                </dl>
                <div className="mt-3 flex gap-2">
                  <Btn onClick={() => setEditing(p)}><Pencil className="h-3.5 w-3.5" /> Editar</Btn>
                  {p.status !== 'archivado' && <Btn kind="danger" onClick={() => archive(p)}><Archive className="h-3.5 w-3.5" /> Archivar</Btn>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <ProgramForm
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={(p) => { setEditing(null); setWarnings(p.warnings ?? []); void load(); }}
        />
      )}
    </div>
  );
}
